import { HistoryItem } from '@reporting/rp-types/core';
import rp, { MarkdownEditor, MarkdownEditorAdapter, MarkdownEditorOptions, helpers } from "@reporting/rp-app-host";
import { Page, File as WikiFile } from '@reporting/rp-types/wiki';
import { buildWikiImageUrl, buildWikiPageUrl, findPageByName, isExternalLink, splitLinkTarget } from '../helpers';
import { MarkdownLinkEvent } from '@omegagrid/markdown';

export class PageEditorAdapter extends MarkdownEditorAdapter<MarkdownEditor> {

	constructor(
		private a: number,
		private page: Page,
	) { super() }

	async save(content: string) {
		const updatedPage: Page = {
			idz: this.page.idz,
			id_wiki_page: this.page.id_wiki_page,
			lang: this.page.lang,
			content: content,
		};
		await rp.ms('wiki').post(`pages`, updatedPage, { accounting: this.a });
	}

	async uploadFile(file: File): Promise<string> {
		const base64Content = await helpers.fileToDataUrl(file);
		const uploadFolder = this._getUploadFolder();
		const wikiFile: WikiFile = {
			filename: uploadFolder + this._normalizeFilename(file.name),
			content: base64Content.split(',')[1],
			size: file.size,
		};
		const result = await rp.ms('wiki').post<WikiFile>(`files`, wikiFile, { accounting: this.a });
		if (!result) return Promise.reject('Upload failed');
		return result.filename.split('/').pop();
	}

	async getContent(): Promise<string> {
		return await rp.ms('wiki').get<string>(
			`pages/${this.page.id_wiki_page}/content`, { lang: this.page.lang, accounting: this.a }
		);
	}

	async getHistoryContent(idz: number): Promise<string> {
		return rp.ms('wiki').get<string>(
			`pages/${this.page.id_wiki_page}/history/${idz}`, { accounting: this.a }
		);
	}

	getHistoryItems(): Promise<HistoryItem[]> {
		return rp.ms('wiki').get<HistoryItem[]>(
			`pages/${this.page.id_wiki_page}/history`, { lang: this.page.lang, accounting: this.a }
		);
	}

	getOptions(): MarkdownEditorOptions {
		return this.createOptions({
			history: true,
		});
	}

	getCurrentItem(): HistoryItem {
		return this.page;
	}

	modifyImageUrl(url: string): string {
		return /^blob:/.test(url) ? url : buildWikiImageUrl(this.a, url);
	}

	/** The rendered href of a link to another page is the address of that page (see PageContainer), the markdown keeps the page name */
	modifyLinkUrl(href: string): string {
		if (!href || href.startsWith('#') || isExternalLink(href)) return href;
		const { name, anchor } = splitLinkTarget(href);
		return buildWikiPageUrl(this.a, name, anchor);
	}

	async onLink(e: MarkdownLinkEvent): Promise<void> {
		// the destination as written in the markdown, whatever modifyLinkUrl made of the href
		const href = e.href ?? e.a.getAttribute('href');
		if (!href) return;
		if (href.startsWith('#')) {
			this.editor.markdown?.scrollToSelector(href);
			return;
		}
		if (isExternalLink(href)) {
			window.open(href, '_blank');
			return;
		}
		const { name, anchor } = splitLinkTarget(href);
		const page = await findPageByName(this.a, name, this.page.lang);
		if (!page) return;
		const editor = await rp.host.open<MarkdownEditor>(`wiki-pageEditor-${this.a}-${this.page.lang}-${page.id_wiki_page}`, { title: `[${this.page.lang}] ${page.name}` });
		if (anchor && editor?.markdown) editor.markdown.anchor = anchor;
	}

	_getUploadFolder(): string {
		let pageFolder = this.page.name.split('/').slice(0, -1).join('/');
		if (pageFolder) pageFolder += '/';
		pageFolder += 'img/';
		return pageFolder;
	}

	_normalizeFilename(filename: string): string {
		if (filename === 'image.png') { // default name for pasted images from clipboard, rename to avoid conflicts
			filename = `${this.page.name.split('/').pop()}.png`
		}
		const normalized = filename.normalize('NFD').replace(/\p{M}/gu, '');
		return normalized.replace(/[^a-zA-Z0-9._-]/g, '_');
	}
}
